import { create } from 'zustand';
import * as chatbotService from '@/services/Common/ChatbotService';
import type { ChatbotConversation, ChatbotMessage } from '@/services/Common/ChatbotService';
import ticketServiceInstance from '@/services/Common/TicketService';
import type { Ticket } from '@/services/Common/TicketService';

type ActiveView = 'chat' | 'conversations' | 'tickets';

interface ChatbotState {
  isOpen: boolean;
  activeView: ActiveView;
  conversations: ChatbotConversation[];
  activeConversationUuid: string | null;
  messages: ChatbotMessage[];
  isLoading: boolean;
  isSending: boolean;
  tickets: Ticket[];
  loadingTickets: boolean;

  toggleWidget: () => void;
  openWidget: () => void;
  closeWidget: () => void;
  setActiveView: (view: ActiveView) => void;
  fetchConversations: () => Promise<void>;
  startNewConversation: () => Promise<void>;
  loadConversation: (uuid: string) => Promise<void>;
  sendMessage: (message: string) => Promise<void>;
  escalateToTicket: (subject: string, category: string) => Promise<void>;
  closeConversation: () => Promise<void>;
  fetchTickets: () => Promise<void>;
  clearActiveConversation: () => void;
}

const useChatbotStore = create<ChatbotState>((set, get) => ({
  isOpen: false,
  activeView: 'chat',
  conversations: [],
  activeConversationUuid: null,
  messages: [],
  isLoading: false,
  isSending: false,
  tickets: [],
  loadingTickets: false,

  toggleWidget: () => set(state => ({ isOpen: !state.isOpen })),
  openWidget: () => set({ isOpen: true }),
  closeWidget: () => set({ isOpen: false }),
  setActiveView: (view) => set({ activeView: view }),

  fetchConversations: async () => {
    set({ isLoading: true });
    try {
      const conversations = await chatbotService.getConversations();
      set({ conversations, isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  startNewConversation: async () => {
    set({ isLoading: true });
    try {
      const conversation = await chatbotService.createConversation();
      set(state => ({
        activeConversationUuid: conversation.uuid,
        messages: [],
        conversations: [conversation, ...state.conversations],
        activeView: 'chat',
        isLoading: false,
      }));
    } catch {
      set({ isLoading: false });
    }
  },

  loadConversation: async (uuid) => {
    set({ isLoading: true });
    try {
      const result = await chatbotService.getConversation(uuid);
      set({
        activeConversationUuid: uuid,
        messages: result.messages,
        activeView: 'chat',
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  sendMessage: async (message) => {
    const { activeConversationUuid } = get();
    if (!activeConversationUuid) return;

    // Optimistic: add user message immediately
    const userMsg: ChatbotMessage = {
      role: 'user',
      content: message,
      created_at: new Date().toISOString(),
    };
    set(state => ({ messages: [...state.messages, userMsg], isSending: true }));

    try {
      const result = await chatbotService.sendMessage(activeConversationUuid, message);
      set(state => ({
        messages: [...state.messages, result.message],
        isSending: false,
      }));
    } catch {
      // Add error message
      const errorMsg: ChatbotMessage = {
        role: 'assistant',
        content: "Sorry, I couldn't process your message. Please try again.",
        created_at: new Date().toISOString(),
      };
      set(state => ({
        messages: [...state.messages, errorMsg],
        isSending: false,
      }));
    }
  },

  escalateToTicket: async (subject, category) => {
    const { activeConversationUuid } = get();
    if (!activeConversationUuid) return;

    set({ isSending: true });
    try {
      const ticket = await chatbotService.escalateToTicket(activeConversationUuid, { subject, category });
      const sysMsg: ChatbotMessage = {
        role: 'system',
        content: `Support ticket ${ticket.ticket_number} has been created. Our team will follow up shortly.`,
        metadata: { ticket: { id: ticket.id, ticket_number: ticket.ticket_number } },
        created_at: new Date().toISOString(),
      };
      set(state => ({
        messages: [...state.messages, sysMsg],
        isSending: false,
      }));
    } catch {
      set({ isSending: false });
    }
  },

  closeConversation: async () => {
    const { activeConversationUuid } = get();
    if (!activeConversationUuid) return;

    try {
      await chatbotService.closeConversation(activeConversationUuid);
      set(state => ({
        conversations: state.conversations.map(c =>
          c.uuid === activeConversationUuid ? { ...c, status: 'closed' as const } : c
        ),
      }));
    } catch { /* ignore */ }
  },

  fetchTickets: async () => {
    set({ loadingTickets: true });
    try {
      const tickets = await ticketServiceInstance.getUserTickets();
      set({ tickets, loadingTickets: false });
    } catch {
      set({ loadingTickets: false });
    }
  },

  clearActiveConversation: () => set({
    activeConversationUuid: null,
    messages: [],
    activeView: 'conversations',
  }),
}));

export default useChatbotStore;
